import { FeatureCollection, Position } from 'geojson';
import moment from 'moment';
import { atom, atomFamily, DefaultValue, selector, selectorFamily } from 'recoil';
import { CONSUMPTION_REPORTS_PAGE, DELIVERY_REPORTS_PAGE, EVIDENCES_REPORTS_PAGE } from '../constants/app';
import { REPORT_INTERVAL_OPTIONS, REPORT_TYPE_OPTIONS } from '../constants/reports';
import { HistoricAlarm, HistoricDoorState, HistoricState, HistoricTelemetry } from '../types/currentState';
import { EvidenceMedia } from '../types/evidences';
import { NotificationType } from '../types/notification';
import { Path, PathItem, PathItemByGroup } from '../types/paths';
import { DeliveryReport } from '../types/reports';
import { getLastMonthsList } from '../utils/dates';
import { isValidSafegptAlarmPoint } from '../utils/map';
import { pageState } from './app';

export const loadingReportRouteState = atom({
  key: 'app.reports.routeEvents.loading',
  default: false,
});

export const activeReportRouteState = atom<string | undefined>({
  key: 'app.reports.active',
  default: undefined,
});

export const selectedReportRowState = atom<number | undefined>({
  key: 'app.reports.row.selected',
  default: undefined,
});

export const loadingTelemetryState = atom({
  key: 'app.reports.telemetry.loading',
  default: false,
});

export const activeTelemetryState = atom<string | undefined>({
  key: 'app.reports.telemetry.active',
  default: undefined,
});

export const updateReportsState = atom<boolean>({
  key: 'app.reports.update',
  default: false,
});

export const reportFilterGroupState = atom<string>({
  key: 'app.reports.filter.group',
  default: '',
});

export const reportSearchTypeState = atom<number>({
  key: 'app.reports.searchType',
  default: 0,
});

export const reportViewState = atom<number>({
  key: 'app.reports.groups.view',
  default: 0,
});

export const reportIntervalState = atom<number>({
  key: 'app.reports.interval.value',
  default: REPORT_INTERVAL_OPTIONS[0].id,
});

export const reportIsManualIntervalState = atom<boolean>({
  key: 'app.reports.manualInterval.active',
  default: false,
});

export const reportNameState = selector<string>({
  key: 'app.reports.name',
  get: ({ get }) => {
    const page = get(pageState);
    const report = REPORT_TYPE_OPTIONS.find((report) => {
      return report.children ? report.children.some((child) => child.id === page) : report.id === page;
    });
    return (report?.children ? report.children.find((child) => child.id === page) : report)?.label ?? '';
  },
});

export const reportSelectedAssetIdsState = atom<string[] | undefined>({
  key: 'app.reports.selectedAssetIds',
  default: undefined,
});

export const reportStartDateState = atom<number | null | undefined>({
  key: 'app.reports.startTime',
  default: undefined,
});

export const reportEndDateState = atom<number | null | undefined>({
  key: 'app.reports.endTime',
  default: undefined,
});

export const reportMaxEndDateState = atom<number | undefined>({
  key: 'app.reports.maxEndDate',
  default: undefined,
});

export const reportMinStartDateState = atom<number | undefined>({
  key: 'app.reports.minStartDate',
  default: undefined,
});

export const reportHistoricStates = atom<{ [key: string]: { states: HistoricState[]; onlyEvents: boolean } }>({
  key: 'app.reports.historicStates',
  default: {},
});

export const reportHistoricState = selectorFamily<{ states: HistoricState[]; onlyEvents: boolean } | undefined, string>(
  {
    key: 'app.reports.historicState',
    get:
      (assetId) =>
      ({ get }) => {
        const historic = get(reportHistoricStates)[assetId];
        if (!historic?.states?.length) return;

        if (historic?.onlyEvents) {
          let newHistoric = { ...historic };
          const notificationType = get(selectedNotificationTypeState(assetId));
          if (notificationType === undefined) {
            newHistoric.states = newHistoric.states.filter((state) => {
              const notificationType = get(notificationTypeState(state.notificationType));
              return notificationType?.isEvent;
            });
          } else {
            newHistoric.states = newHistoric.states.filter((state) => {
              return state.notificationType === notificationType;
            });
          }
          return newHistoric;
        }

        return historic;
      },
  },
);

export const reportTelemetryStates = atom<{ [key: string]: HistoricTelemetry[] }>({
  key: 'app.reports.telemetry',
  default: {},
});

export const reportTelemetryState = selectorFamily<HistoricTelemetry[] | undefined, string>({
  key: 'app.reports.telemetry',
  get:
    (assetId) =>
    ({ get }) => {
      const historic = get(reportTelemetryStates)[assetId];
      if (!historic?.length) return;

      return historic;
    },
});

export const reportTelemetryLngLatsState = selectorFamily<Position[], string>({
  key: 'app.reports.reportTelemetryLngLats',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(reportTelemetryState(assetId));
      if (!states?.length) return [];
      return states.map((state) => [state.position_longitude, state.position_latitude]).reverse();
    },
});

export const loadingDoorState = atom({
  key: 'app.reports.door.loading',
  default: false,
});

export const activeDoorState = atom<string | undefined>({
  key: 'app.reports.door.active',
  default: undefined,
});

export const reportDoorStates = atom<{ [key: string]: HistoricDoorState[] }>({
  key: 'app.reports.door',
  default: {},
});

export const reportDoorState = selectorFamily<HistoricDoorState[] | undefined, string>({
  key: 'app.reports.door',
  get:
    (assetId) =>
    ({ get }) => {
      const historics = get(reportDoorStates);
      if (assetId) {
        const historic = historics[assetId];
        if (!historic?.length) return;
        return historic;
      }
      return Object.values(historics).flat();
    },
});

export const reportDoorLngLatsState = selectorFamily<Position[], string>({
  key: 'app.reports.reportDoorLngLats',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(reportDoorState(assetId));
      if (!states?.length) return [];
      return states.map((state) => [state.position_longitude, state.position_latitude]).reverse();
    },
});

export const fullReportHistoricState = selectorFamily<
  { states: HistoricState[]; onlyEvents: boolean } | undefined,
  string
>({
  key: 'app.reports.historicState.full',
  get:
    (assetId) =>
    ({ get }) => {
      const historic = get(reportHistoricStates)[assetId];
      if (!historic?.states?.length) return;

      return historic;
    },
  set:
    (assetId) =>
    ({ get, set }, newValue) => {
      const historicStates = get(reportHistoricStates);
      const newHistoricState = { ...historicStates[assetId] };
      if (newValue instanceof DefaultValue || !newValue) {
        newHistoricState.onlyEvents = false;
      } else {
        newHistoricState.onlyEvents = newValue.onlyEvents;
      }
      const newHistorics = { ...historicStates, [assetId]: newHistoricState };
      set(reportHistoricStates, newHistorics);
    },
});

export const reportHistoricLngLatsState = selectorFamily<Position[], string>({
  key: 'app.reports.historicLngLatsState',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(reportHistoricState(assetId))?.states;
      if (!states?.length) return [];
      return states.map((state) => [state.position_longitude, state.position_latitude]).reverse();
    },
});

export const reportHistoricInteractiveState = selectorFamily<FeatureCollection, string>({
  key: 'app.reports.historicLngLatsState.interactive',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(reportHistoricState(assetId))?.states;
      return {
        type: 'FeatureCollection',
        features: states?.length
          ? states.toReversed().map((state) => {
              const notificationType = get(notificationTypeState(state.notificationType));
              return {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [state.position_longitude, state.position_latitude],
                },
                properties: {
                  name: notificationType?.name,
                },
              };
            })
          : [],
      };
    },
});

export const reportAverageSpeedState = selectorFamily<string, string>({
  key: 'app.reports.historicStates.averageSpeed',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(fullReportHistoricState(assetId))?.states.filter((state) => state.position_speed > 0);
      if (!states?.length) return 'N/A';

      const historicSpeed = states.reduce((prev, curr) => prev + curr.position_speed, 0);
      const averageSpeed = (historicSpeed / states.length).toFixed(2);
      return `${averageSpeed} Km/h`;
    },
});

export const reportDistanceTraveledState = selectorFamily<string, string>({
  key: 'app.reports.historicStates.distanceTraveled',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(fullReportHistoricState(assetId))?.states;
      if (!states?.length) return 'N/A';

      const initialMileage = states[0].gnss_vehicle_mileage;
      const lastMileage = states[states.length - 1].gnss_vehicle_mileage;
      const distanceTraveled = (lastMileage - initialMileage).toFixed(2);
      return `${distanceTraveled} Km`;
    },
});

export const notificationTypesState = atom<NotificationType[]>({
  key: 'app.reports.notificationTypes',
  default: [],
});

export const notificationTypeState = selectorFamily<NotificationType | undefined, number>({
  key: 'app.reports.notificationType',
  get:
    (id) =>
    ({ get }) => {
      const notificationTypes = get(notificationTypesState);

      return notificationTypes.find((notificationType) => notificationType.notificationId === id);
    },
});

export const selectedNotificationTypeState = atomFamily<number | undefined, string>({
  key: 'app.reports.notificationTypes.selected',
  default: undefined,
});

export const activePathState = atom<string | undefined>({
  key: 'app.reports.paths.active',
  default: undefined,
});

export const selectedPathIdState = atom<string | undefined>({
  key: 'app.reports.selectedPathId',
  default: undefined,
});

export const pathState = atom<Path | undefined>({
  key: 'app.reports.path',
  default: undefined,
});

export const updatePathState = atom<boolean>({
  key: 'app.reports.path.update',
  default: false,
});

export const pathsState = atom<PathItem[] | PathItemByGroup[] | undefined>({
  key: 'app.reports.paths',
  default: undefined,
});

export const selectedPathState = selector<PathItem | undefined>({
  key: 'app.reports.selectedPath',
  get: ({ get }) => {
    const selectedPathId = get(selectedPathIdState);
    if (!selectedPathId) return;
    const paths = get(pathsState);
    if (!paths?.length) return;
    return paths.find((path) => path._id === selectedPathId);
  },
});

export const pathTimeFilterState = atom<number>({
  key: 'app.reports.paths.filter.time',
  default: 5,
});

export const pathDistanceFilterState = atom<number>({
  key: 'app.reports.paths.filter.distance',
  default: 700,
});

export const updateReportState = atom<boolean>({
  key: 'app.report.update',
  default: false,
});

export const canUpdateReportState = selector<boolean>({
  key: 'app.report.canUpdate',
  get: ({ get }) => {
    const page = get(pageState);
    const selectedAssetIds = get(reportSelectedAssetIdsState);
    const groupId = get(reportFilterGroupState);
    const endDate = get(reportEndDateState);
    const startDate = get(reportStartDateState);
    if (
      page !== EVIDENCES_REPORTS_PAGE.id &&
      page !== CONSUMPTION_REPORTS_PAGE.id &&
      page !== DELIVERY_REPORTS_PAGE.id
    ) {
      return (!!selectedAssetIds?.length || !!groupId) && !!endDate && !!startDate;
    }
    if (page === DELIVERY_REPORTS_PAGE.id) {
      const date = get(deliveryDateState);
      return !!date;
    }
    return true;
  },
});

export const loadingConsumptionState = atom({
  key: 'app.reports.consumption.loading',
  default: false,
});

export const consumptionDateState = atom<string>({
  key: 'app.reports.consumption.date',
  default: getLastMonthsList(1)[0].id,
});

// historic evidence report
export const loadingEvidenceState = atom({
  key: 'app.reports.evidence.loading',
  default: false,
});

export const activeEvidenceState = atom<string | undefined>({
  key: 'app.reports.evidence.active',
  default: undefined,
});

export const reportEvidenceStates = atom<{ [key: string]: EvidenceMedia[] }>({
  key: 'app.reports.evidences',
  default: {},
});

export const reportEvidenceState = selectorFamily<EvidenceMedia[] | undefined, string>({
  key: 'app.reports.evidence',
  get:
    (assetId) =>
    ({ get }) => {
      const historic = get(reportEvidenceStates)[assetId];
      if (!historic?.length) return;

      return historic;
    },
});

export const evidenceMediaTypeFilterState = atom<string | undefined>({
  key: 'app.reports.evidence.filter.mediaType',
  default: undefined,
});

export const evidenceOriginFilterState = atom<string | undefined>({
  key: 'app.reports.evidence.filter.origin',
  default: undefined,
});

export const evidenceAlarmFilterState = atom<number | undefined>({
  key: 'app.reports.evidence.filter.alarm',
  default: undefined,
});

// Delivery
export const deliveryDateState = atom<Date>({
  key: 'app.reports.delivery.date',
  default: moment().subtract(1, 'day').toDate(),
});

export const deliveryAllDevicesState = atom<DeliveryReport | undefined>({
  key: 'app.reports.delivery.allDevices',
  default: undefined,
});

export const activeDeliveryState = atom<string | undefined>({
  key: 'app.reports.delivery.active',
  default: undefined,
});

export const deliveryReportStates = atom<{ [key: string]: DeliveryReport[] }>({
  key: 'app.reports.delivery',
  default: {},
});

export const delivertReportState = selectorFamily<DeliveryReport[] | undefined, string>({
  key: 'app.reports.delivery',
  get:
    (assetId) =>
    ({ get }) => {
      const historics = get(deliveryReportStates);
      if (assetId) {
        const historic = historics[assetId];
        if (!historic?.length) return;
        return historic;
      }
      return Object.values(historics).flat();
    },
});

// alarms
export const loadingAlarmsState = atom({
  key: 'app.reports.alarms.loading',
  default: false,
});

export const reportAlarmsStates = atom<{ [key: string]: HistoricAlarm[] }>({
  key: 'app.reports.alarms',
  default: {},
});

export const reportAlarmsState = selectorFamily<HistoricAlarm[] | undefined, string>({
  key: 'app.reports.alarms',
  get:
    (assetId) =>
    ({ get }) => {
      const historic = get(reportAlarmsStates)[assetId];
      if (!historic?.length) return;

      return historic;
    },
});

export const reportAlarmFilterState = atom<number | undefined>({
  key: 'app.reports.alarms.alarmType',
  default: undefined,
});

export const activeReportAlarmState = atom<string | undefined>({
  key: 'app.reports.alarms.active',
  default: undefined,
});

export const reportAlarmLngLatsState = selectorFamily<Position[], string>({
  key: 'app.reports.alarmLngLatsState',
  get:
    (assetId) =>
    ({ get }) => {
      if (!assetId) return [];
      const states = get(reportAlarmsState(assetId));
      if (!states?.length) return [];
      return states
        .filter((state) => isValidSafegptAlarmPoint(state.gpsLng, state.gpsLat))
        .map((state) => [state.gpsLng, state.gpsLat]);
    },
});
